/**
 * Utility types and enums for booklet preview scene configuration.
 * Provides mappings between product layouts, scenes, and preview labels.
 */
// -------------------- Utility Types and Enums --------------------
// This section defines the types and enums used for configuring booklet preview scenes.
// These are used to map product layouts to their respective scenes and preview labels.

// -------------------- Preview Labels --------------------
// PreviewLabel: Enum representing the different labels that can be shown in a booklet preview.
// For example, covers, spine, and pages.
export enum PreviewLabel {
  FrontCover = 'front_cover',
  InsideFrontCover = 'inside_front_cover',
  Page = 'page',
  InsideBackCover = 'inside_back_cover',
  BackCover = 'back_cover',
  Spine = 'spine',
}

// -------------------- Scene Keys --------------------
// SceneKey: Enum representing the unique keys for each scene type in a booklet.
// These are used to identify which scene configuration to use for a given part of the booklet.
export enum SceneKey {
  FrontCover = 'front_cover',
  InnerFrontCoverFirstPage = 'inner_front_cover_first_page',
  DoublePage = 'double_page',
  InnerBackCoverLastPage = 'inner_back_cover_last_page',
  BackCover = 'back_cover',
}

// -------------------- Scene Config Types --------------------
// SceneConfig: Type describing the configuration for a single scene, currently only the scene name.
export type SceneConfig = {
  scene_name: string;
};

// ScenesMap: Partial mapping from SceneKey to SceneConfig, allowing for flexible scene definitions.
export type ScenesMap = Record<SceneKey, SceneConfig>;

// -------------------- Preview Structure Types --------------------
// PreviewStructureItemType: Enum to distinguish between fixed and ranged preview structure items.
// 'Fixed' means a specific set of panels, 'Range' means a sequence of panels.
export enum PreviewStructureItemType {
  Fixed = 'fixed',
  Range = 'range',
}

// PreviewStructureItem: Union type describing either a fixed or ranged preview structure item.
// - Fixed: Used for scenes with a known set of panels and labels.
// - Range: Used for scenes that cover a range of panels, with a repeated label structure.
export type PreviewStructureItem<S extends ScenesMap = ScenesMap> =
  | {
      type: PreviewStructureItemType.Fixed;
      scene: keyof S | null;
      panels: number[];
      labels: PreviewLabel[];
    }
  | {
      type: PreviewStructureItemType.Range;
      scene: keyof S;
      panel_range: [number, number];
      count: number;
      labels: PreviewLabel[];
    };

// -------------------- Layout Config Types --------------------
// LayoutConfig: Type describing the configuration for a booklet layout.
// Includes the scenes mapping and the ordered preview structure for rendering.
export type PreviewLayoutConfig<S extends ScenesMap = ScenesMap> = {
  scenes: S;
  orderedPreviewStructure: PreviewStructureItem<S>[];
};

// FIRST_PANEL and LAST_PANEL: Constants representing the first and last panel indices.
// Used for calculations in preview structure definitions.
export const FIRST_PANEL = 0;
export const LAST_PANEL = -1;

export enum BookletPreviewType {
  FullCover = 'full_cover',
  FrontCover = 'front_cover',
  BackCover = 'back_cover',
  Page = 'page',
}
export interface BookletPreviewPage {
  type: BookletPreviewType;
  previewUrl: string;
  labels?: string[];
  // pageNumbersByPanels?: number[];
  pageNumbersByLabels?: number[];
}
